import React from 'react'
import OrdinalsRender from 'components/answers/FileEmbed/OrdinalsRender'
import useOrdinalDescriptor from 'hooks/useOrdinalDescriptor'

function withPreviewSize(url: string | undefined, size: 'small' | 'medium' | 'large' | 'original') {
    if (!url) return undefined
    try {
        const u = new URL(url, window.location.href)
        // Only mutate our stable Rails preview endpoint; leave external URLs untouched.
        if (/^\/api\/v1\/bsv\/ordinals\/[^\/]+\/preview_image$/i.test(u.pathname || '')) {
            u.searchParams.set('size', size)
            return u.toString()
        }
        return url
    } catch {
        return url
    }
}

export default function ResolvedOrdinalRender({
    treechatOrdinalId,
    treechatPreviewUrl,
    seenOutpoint,
    originOutpointHint,
    fileType,
    className,
    allowInteraction,
    autoCapture3dPreview,
    forceLive,
    onRequestLive,
    htmlAlign = 'top-center',
    assumeOrigin = false,
    fillVideoContainer = false,
}: {
    treechatOrdinalId?: string | number
    treechatPreviewUrl?: string | null
    /** The outpoint we encountered (UTXO/transfer outpoint or origin outpoint) */
    seenOutpoint: string
    /** Optional origin outpoint hint (e.g. from wallet indexer data) */
    originOutpointHint?: string
    fileType?: string
    className?: string
    allowInteraction: boolean
    autoCapture3dPreview?: boolean
    forceLive?: boolean
    onRequestLive?: () => void
    htmlAlign?: 'center' | 'top-center'
    assumeOrigin?: boolean
    fillVideoContainer?: boolean
}) {
    const { data: descriptor } = useOrdinalDescriptor(seenOutpoint, {
        assumeOrigin: !!assumeOrigin,
        originOutpointHint,
        enabled: !!seenOutpoint,
    })

    const url = treechatOrdinalId
        ? `${window.knovApiUrl}/api/v1/bsv/ordinals/${treechatOrdinalId}/data`
        : descriptor?.content_url || ''

    const ct = descriptor?.content_type || fileType || ''
    const basePreviewUrl = descriptor?.preview_url || treechatPreviewUrl || undefined
    const previewUrl =
        allowInteraction && ct.toLowerCase().startsWith('image/')
            ? withPreviewSize(basePreviewUrl, 'original')
            : basePreviewUrl

    // If we don't yet have a backend content_url for non-treechat ordinals, show a placeholder
    // rather than silently falling back to third-party hosts. This ensures we always try to
    // render via Rails (and thus persist BsvVout/BsvOrdinal + cache ordinal_data).
    if (!treechatOrdinalId && !url) {
        return (
            <div
                className={className}
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'rgba(0,0,0,0.55)',
                    fontSize: 12,
                    padding: 8,
                    textAlign: 'center',
                }}
            >
                Resolving ordinal…
            </div>
        )
    }

    const originOutpoint =
        (descriptor?.status === 'resolved' && descriptor.origin_outpoint) ||
        originOutpointHint ||
        seenOutpoint

    return (
        <OrdinalsRender
            isTreechat={!!treechatOrdinalId}
            outpoint={originOutpoint}
            url={url}
            bsvOrdinalId={descriptor?.bsv_ordinal_id || undefined}
            contentType={ct}
            filename={descriptor?.filename || undefined}
            previewUrl={previewUrl}
            className={className}
            allowInteraction={allowInteraction}
            useBabylonForGltf={true}
            autoCapture3dPreview={!!autoCapture3dPreview}
            onRequestLive={onRequestLive}
            forceLive={!!forceLive}
            htmlAlign={htmlAlign}
            fillVideoContainer={fillVideoContainer}
        />
    )
}
